import React, { useCallback, useState } from 'react';
import { position, rem, rgba, size } from 'polished';
import styled, { css } from 'styled-components';

import { breakpoints } from 'helpers/styles/constants';
import GenericCollapse from 'components/GenericCollapse/GenericCollapse';

const focusedTitle = css`
  color: ${({ theme }) => theme.colors.accent};

  &::before {
    border-top-color: ${({ theme }) => theme.colors.accent};
  }
`;

const TitleButton = styled.button<{ isOpened?: boolean }>`
  position: relative;
  display: block;
  width: 100%;
  margin-top: 0;
  padding: ${rem(26)} ${rem(26)} ${rem(26)} 0;
  text-align: left;
  border: 1px solid ${({ theme }) => rgba(theme.colors.baliHai, 0.5)};
  border-right-width: 0;
  border-left-width: 0;
  outline: none;
  transition: 0.3s ease color, 0.3s ease border-color;

  ${({ isOpened, theme }) =>
    isOpened &&
    css`
      ${focusedTitle};
      border-color: ${rgba(theme.colors.baliHai, 0)};

      &::before {
        transform: rotate(180deg);
      }
    `};

  &:focus {
    ${focusedTitle};
  }

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    right: ${rem(10)};
    ${size(0)};
    margin-top: ${rem(-2)};
    border-color: ${({ theme }) => theme.colors.main} transparent transparent;
    border-style: solid;
    border-width: ${rem(5)} ${rem(5)} 0;
    opacity: 0.7;
    transition: 0.3s ease transform, 0.3s ease border-top-color;
  }
`;

const Content = styled.div`
  position: relative;
  padding: ${rem(15)};
  line-height: 1.9;

  @media ${breakpoints.smUp} {
    padding: ${rem(45)} ${rem(40)} ${rem(65)};
  }

  > div {
    position: relative;
  }

  & & {
    border: 1px solid ${({ theme }) => theme.colors.bermudaGrayLight};

    @media ${breakpoints.smUp} {
      padding: ${rem(40)};
    }
  }

  &::before {
    ${position('absolute', 0, 0, 0, 0)};
    content: '';
    pointer-events: none;
    background: linear-gradient(
      180deg,
      ${({ theme }) => theme.colors.catshillWhite} 0%,
      ${({ theme }) => theme.colors.aquaHaze} 100%
    );
    opacity: 0.5;
  }

  hr {
    margin: ${rem(40)} 0 ${rem(30)};
    border-bottom: 0;
    border-color: ${({ theme }) => theme.colors.bermudaGray};
  }

  ul {
    padding-left: ${rem(20)};
    list-style-type: disc;
  }

  li > ul {
    list-style-type: circle;
  }

  li > p + ul {
    margin-top: ${rem(-10)};
  }

  a {
    text-decoration: underline;
    color: ${({ theme }) => theme.colors.accent};
    transition: color 0.2s ease;

    &:hover {
      color: ${({ theme }) => theme.colors.accentHover};
    }
  }

  table {
    display: block;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    width: 100%;
    font-size: ${({ theme }) => theme.fontSizes.table};
    line-height: 1.3em;
    border-spacing: 0;

    @media ${breakpoints.smUp} {
      /* http://quantityqueries.com/ */
      th:nth-last-child(n + 3):nth-last-child(-n + 3):first-child,
      th:nth-last-child(n + 3):nth-last-child(-n + 3):first-child ~ th {
        width: 33%;
      }
    }

    td,
    th {
      padding: 5px 5px 5px 10px;
    }

    th {
      min-width: 180px;
      text-align: left;
    }

    /* stylelint-disable no-descending-specificity */

    tr:not(:last-child) td,
    th {
      border-bottom: 1px solid ${({ theme }) => theme.colors.bermudaGrayLight};
    }

    td:not(:last-child),
    th:not(:last-child) {
      border-right: 1px solid ${({ theme }) => theme.colors.bermudaGrayLight};
    }

    ul {
      padding: 0;
      list-style: none;
    }

    /* stylelint-enable no-descending-specificity */
  }
`;

const ChildrenContent = styled.div`
  margin-top: ${rem(30)};
`;

const CollapseItem = styled.div`
  margin-top: ${rem(-1)};

  &:first-child {
    ${TitleButton} {
      border-top-width: 0;
    }
  }

  &:last-child {
    ${TitleButton} {
      border-bottom-width: 0;
    }
  }
`;

interface Props {
  title: React.ReactNode;
  description: React.ReactNode;
  children?: React.ReactNode;
}

export default function CollapseText({ title, description, children }: Props) {
  const [isOpened, setIsOpened] = useState(false);
  const onToggle = useCallback(() => setIsOpened(prevValue => !prevValue), []);

  return (
    <CollapseItem>
      <TitleButton data-test="title-button" onClick={onToggle}>
        {title}
      </TitleButton>

      <GenericCollapse isOpened={isOpened}>
        <Content data-test="collapse-content">
          <div>{description}</div>

          {children && <ChildrenContent>{children}</ChildrenContent>}
        </Content>
      </GenericCollapse>
    </CollapseItem>
  );
}
